export interface EmailTemplateConfig {
  subject: string;
  body: string;
  variables: string[];
}

export const templateMeta: Record<string, { label: string; desc: string; group: string; variables: string[]; hasDelay?: boolean }> = {
  signup: {
    label: "Registreringsbekräftelse",
    desc: "Skickas efter kontoregistrering",
    group: "AUTENTISERING",
    variables: ["club_name", "user_name"],
  },
  recovery: {
    label: "Lösenordsåterställning",
    desc: "Skickas vid 'Glömt lösenord'",
    group: "AUTENTISERING",
    variables: ["club_name", "user_name"],
  },
  magiclink: {
    label: "Magisk inloggningslänk",
    desc: "Skickas vid lösenordslös inloggning",
    group: "AUTENTISERING",
    variables: ["club_name", "user_name"],
  },
  invite: {
    label: "Inbjudan till klubben",
    desc: "Skickas till inbjudna nya medlemmar",
    group: "AUTENTISERING",
    variables: ["club_name", "user_name"],
  },
  email_change: {
    label: "Bekräfta ny e-post",
    desc: "Skickas vid e-postbyte",
    group: "AUTENTISERING",
    variables: ["club_name", "user_name"],
  },
  booking_confirmation: {
    label: "Bokningsbekräftelse",
    desc: "Skickas direkt efter bokning",
    group: "BOKNING",
    variables: ["playerName", "courtName", "date", "startTime", "endTime", "price", "clubName"],
  },
  booking_cancellation: {
    label: "Avbokningsbekräftelse",
    desc: "Skickas vid avbokning",
    group: "BOKNING",
    variables: ["playerName", "courtName", "date", "startTime", "endTime", "clubName"],
  },
  booking_cancellation_bulk: {
    label: "Avbokningsbekräftelse (flera)",
    desc: "Skickas när flera bokningar avbokas samtidigt (PR-AK Q5: 1 mail per kund)",
    group: "BOKNING",
    variables: ["playerName", "bookingsList", "count", "clubName"],
  },
  booking_reminder: {
    label: "Påminnelse före match",
    desc: "Skickas X timmar innan",
    group: "BOKNING",
    variables: ["playerName", "courtName", "date", "startTime", "endTime", "hoursUntil", "clubName"],
    hasDelay: true,
  },
  waitlist_offer: {
    label: "Väntelista — tid ledig",
    desc: "Skickas när en köplats erbjuds",
    group: "BOKNING",
    variables: ["playerName", "courtName", "date", "startTime", "endTime", "minutesLeft", "bookingUrl", "clubName"],
  },
  tournament_invite: {
    label: "Turneringsinbjudan",
    desc: "Skickas till inbjudna spelare",
    group: "TURNERINGAR",
    variables: ["playerName", "tournamentName", "format", "date", "clubName"],
  },
  tournament_result: {
    label: "Turneringsresultat",
    desc: "Skickas efter avslutad turnering",
    group: "TURNERINGAR",
    variables: ["playerName", "tournamentName", "placement", "clubName"],
  },
  training_reminder: {
    label: "Träningspåminnelse",
    desc: "Skickas inför träningspass",
    group: "TRÄNING & LEKTIONER",
    variables: ["playerName", "groupName", "coachName", "date", "startTime", "endTime", "courtName", "clubName"],
  },
  training_cancelled: {
    label: "Träning inställd",
    desc: "Skickas om pass ställs in",
    group: "TRÄNING & LEKTIONER",
    variables: ["playerName", "groupName", "date", "startTime", "clubName", "reason"],
  },
  lesson_confirmation: {
    label: "Lektionsbekräftelse",
    desc: "Skickas vid bokad privatlektion",
    group: "TRÄNING & LEKTIONER",
    variables: ["playerName", "coachName", "date", "startTime", "endTime", "price", "clubName"],
  },
  lesson_reminder: {
    label: "Lektionspåminnelse",
    desc: "Skickas inför lektion",
    group: "TRÄNING & LEKTIONER",
    variables: ["playerName", "coachName", "date", "startTime", "clubName"],
  },
  league_match_scheduled: {
    label: "Match schemalagd",
    desc: "Skickas när seriematch bokas",
    group: "SERIESPEL",
    variables: ["playerName", "homeTeam", "awayTeam", "date", "startTime", "courtName", "clubName"],
  },
  league_result_reminder: {
    label: "Rapportera resultat",
    desc: "Påminnelse att rapportera",
    group: "SERIESPEL",
    variables: ["playerName", "homeTeam", "awayTeam", "date", "clubName"],
  },
  league_team_invite: {
    label: "Laginbjudan",
    desc: "Skickas när en spelare bjuder in en medspelare till seriespel",
    group: "SERIESPEL",
    variables: ["playerName", "inviteName", "leagueName", "inviteUrl", "clubName"],
  },
};

export const defaultTemplates: Record<string, { subject: string; body: string }> = {
  signup: {
    subject: "Välkommen till {{club_name}}! 🎾",
    body: "Vad kul att du vill spela med oss! Klicka nedan för att bekräfta din e-postadress.",
  },
  recovery: {
    subject: "Återställ ditt lösenord",
    body: "Ingen fara! Klicka nedan så kan du välja ett nytt.",
  },
  magiclink: {
    subject: "Din inloggningslänk",
    body: "Klicka nedan för att logga in direkt — ingen kod behövs.",
  },
  invite: {
    subject: "Du är inbjuden till {{club_name}}!",
    body: "Du har blivit inbjuden att gå med. Klicka nedan för att komma igång.",
  },
  email_change: {
    subject: "Bekräfta din nya e-postadress",
    body: "Du har begärt att byta e-postadress. Bekräfta genom att klicka nedan.",
  },
  booking_confirmation: {
    subject: "Bokningsbekräftelse – {clubName}",
    body: "Hej {playerName}!\n\nDin bokning är bekräftad:\n\n📅 {date}\n⏰ {startTime}–{endTime}\n🏸 {courtName}\n💰 {price} kr\n\nVälkommen!\n{clubName}",
  },
  booking_cancellation: {
    subject: "Avbokningsbekräftelse – {clubName}",
    body: "Hej {playerName}!\n\nDin bokning har avbokats:\n\n📅 {date}\n⏰ {startTime}–{endTime}\n🏸 {courtName}\n\nMvh,\n{clubName}",
  },
  booking_cancellation_bulk: {
    subject: "{count} bokningar avbokade – {clubName}",
    body: "Hej {playerName}!\n\nFöljande {count} bokningar har avbokats:\n\n{bookingsList}\n\nKontakta klubben om du har frågor.\n\nMvh,\n{clubName}",
  },
  booking_reminder: {
    subject: "Påminnelse: Match om {hoursUntil} timmar – {clubName}",
    body: "Hej {playerName}!\n\nPåminnelse om din bokning:\n\n📅 {date}\n⏰ {startTime}–{endTime}\n🏸 {courtName}\n\nVi ses!\n{clubName}",
  },
  waitlist_offer: {
    subject: "En tid har blivit ledig! – {clubName}",
    body: "Hej {playerName}!\n\nEn tid du har köat på har blivit ledig:\n\n📅 {date}\n⏰ {startTime}–{endTime}\n🏸 {courtName}\n\nDu har {minutesLeft} minuter att boka.\n\nBoka här: {bookingUrl}\n\n{clubName}",
  },
  tournament_invite: {
    subject: "Inbjudan till {tournamentName} – {clubName}",
    body: "Hej {playerName}!\n\nDu är inbjuden till {tournamentName}!\n\n🏆 Format: {format}\n📅 {date}\n\nAnmäl dig via appen.\n\n{clubName}",
  },
  tournament_result: {
    subject: "Resultat: {tournamentName} – {clubName}",
    body: "Hej {playerName}!\n\n{tournamentName} är avslutad.\n\n🏅 Din placering: {placement}\n\nTack för deltagandet!\n{clubName}",
  },
  training_reminder: {
    subject: "Träningspåminnelse – {clubName}",
    body: "Hej {playerName}!\n\nPåminnelse om ditt träningspass:\n\n👥 {groupName}\n👨‍🏫 Tränare: {coachName}\n📅 {date}\n⏰ {startTime}–{endTime}\n🏸 {courtName}\n\nVi ses!\n{clubName}",
  },
  training_cancelled: {
    subject: "Träning inställd – {clubName}",
    body: "Hej {playerName}!\n\nTyvärr har träningspasset ställts in:\n\n👥 {groupName}\n📅 {date}\n⏰ {startTime}\n\nAnledning: {reason}\n\nMvh,\n{clubName}",
  },
  lesson_confirmation: {
    subject: "Lektionsbekräftelse – {clubName}",
    body: "Hej {playerName}!\n\nDin privatlektion är bokad:\n\n👨‍🏫 Tränare: {coachName}\n📅 {date}\n⏰ {startTime}–{endTime}\n💰 {price} kr\n\nVälkommen!\n{clubName}",
  },
  lesson_reminder: {
    subject: "Lektionspåminnelse – {clubName}",
    body: "Hej {playerName}!\n\nPåminnelse om din lektion:\n\n👨‍🏫 Tränare: {coachName}\n📅 {date}\n⏰ {startTime}\n\nVi ses!\n{clubName}",
  },
  league_match_scheduled: {
    subject: "Seriematch schemalagd – {clubName}",
    body: "Hej {playerName}!\n\nEn seriematch har schemalagts:\n\n🏠 {homeTeam} vs 🏃 {awayTeam}\n📅 {date}\n⏰ {startTime}\n🏸 {courtName}\n\nLycka till!\n{clubName}",
  },
  league_result_reminder: {
    subject: "Rapportera matchresultat – {clubName}",
    body: "Hej {playerName}!\n\nGlöm inte att rapportera resultatet för:\n\n🏠 {homeTeam} vs 🏃 {awayTeam}\n📅 {date}\n\nRapportera via appen.\n\n{clubName}",
  },
  league_team_invite: {
    subject: "{playerName} vill spela med dig i {leagueName}",
    body: "Hej!\n\n{playerName} har anmält er som lag i {leagueName} hos {clubName}.\n\nKlicka på länken nedan för att acceptera och gå med i laget:\n\n{inviteUrl}\n\nOm du inte har konto skapas ett automatiskt.\n\nVälkommen!\n{clubName}",
  },
};

// Example data for preview
export const exampleData: Record<string, string> = {
  playerName: "Anna Svensson",
  courtName: "Bana 1",
  date: "Tisdag 15 mars",
  startTime: "18:00",
  endTime: "19:30",
  price: "350",
  clubName: "Padel Arena Stockholm",
  hoursUntil: "2",
  minutesLeft: "15",
  bookingUrl: "https://app.example.com/book?...",
  tournamentName: "Vårturneringen 2026",
  format: "Americano",
  placement: "3:a",
  groupName: "Junior Avancerad",
  coachName: "Erik Johansson",
  reason: "Tränaren är sjuk",
  homeTeam: "Team Alpha",
  awayTeam: "Team Beta",
};

export function renderTemplate(text: string, data: Record<string, string> = exampleData): string {
  return text.replace(/\{(\w+)\}/g, (match, key) => data[key] ?? match);
}
